import React, { useCallback, useEffect, useMemo, useState } from "react";

import Model, { helper } from "@tripian/model";
import {
  Badge,
  CustomSlider,
  PoiSearchAutoComplete,
  SvgIcons,
} from "@tripian/react";

import SearchPlace from "../../../../../../components/SearchPlace/SearchPlace";

import IStepAlternatives from "../../../../../../models/IStepAlternatives";

import useStep from "../../../../../../hooks/useStep";
import useSearchPoi from "../../../../../../hooks/useSearchPoi";
import useFocus from "../../../../../../hooks/useFocus";
import classes from "./SearchContainer.module.scss";
import { CloneModal } from "../../../../../../components/CloneModal/CloneModal";

interface PlanIdStepDay {
  planId: number;
  step: Model.Step;
  dayIndex: number;
}

interface ISearchContainer {
  poiCategoryGroups: Model.PoiCategoryGroup[];
  show: boolean;
  dayIndex: number;
  tripHash: string;
  tripReadOnly: boolean;
  plans: Model.Plan[];
  alternatives: IStepAlternatives[];
  gygTourIds: number[];
  bbTourIds: number[];
  viatorTourIds: string[];
  toristyTourIds: string[];
  toursLoading: boolean;
  onBookingClick: () => void;
  t: (value: Model.TranslationKey) => string;
}

const SearchContainer: React.FC<ISearchContainer> = ({
  poiCategoryGroups,
  show,
  dayIndex,
  tripHash,
  tripReadOnly,
  plans,
  alternatives,
  gygTourIds,
  bbTourIds,
  viatorTourIds,
  toristyTourIds,
  toursLoading,
  onBookingClick,
  t,
}) => {
  const [autoCompleteTags, setAutoCompleteTags] = useState<
    { value: string; label: string }[]
  >([]);
  const [autoCompleteLoading, setAutoCompleteLoading] =
    useState<boolean>(false);
  const [showCloneModal, setShowCloneModal] = useState<boolean>(false);
  const [poiCategorySearchText, setPoiCategorySearchText] =
    useState<string>("");
  const [showFilters, setShowFilters] = useState<boolean>(false);
  const [activeMainCategory, setActiveMainCategory] = useState<string>("all");
  const [selectedTags, setSelectedTags] = useState<string[]>([]);

  const { focusPoi, focusStep } = useFocus();
  const { openSearchPoi, searchPoiAutoCompleteTags } = useSearchPoi();
  const { stepAdd, stepDelete } = useStep();

  const [selectedPoiCategories, setSelectedPoiCategories] = useState<
    Model.PoiCategory[]
  >(
    poiCategoryGroups.flatMap(
      (categoryGroup: Model.PoiCategoryGroup) => categoryGroup.categories
    )
  );

  const selectedPoiCategoryIds = useMemo(
    () => selectedPoiCategories.map(cat => cat.id),
    [selectedPoiCategories]
  );

  useEffect(() => {
    setSelectedPoiCategories(
      poiCategoryGroups.flatMap(
        (categoryGroup: Model.PoiCategoryGroup) => categoryGroup.categories
      )
    );
  }, [poiCategoryGroups]);

  useEffect(() => {
    if (selectedPoiCategoryIds && selectedPoiCategoryIds.length > 0) {
      setAutoCompleteLoading(true);
      searchPoiAutoCompleteTags(selectedPoiCategoryIds)
        .then((options: { value: string; label: string }[]) => {
          setAutoCompleteTags(options);
        })
        .catch(error => {
          setAutoCompleteTags([]);
        })
        .finally(() => {
          setAutoCompleteLoading(false);
        });
    } else {
      setAutoCompleteTags([]);
      setAutoCompleteLoading(false);
    }
  }, [searchPoiAutoCompleteTags, selectedPoiCategoryIds]);

  const currentPlanId = useMemo(
    () => (dayIndex < plans.length ? plans[dayIndex].id : 0),
    [dayIndex, plans]
  );

  const planStepDays: PlanIdStepDay[] = useMemo(() => {
    let steps: PlanIdStepDay[] = [];

    steps = plans.reduce(
      (
        previousValue: PlanIdStepDay[],
        currentValue: Model.Plan,
        currentIndex: number
      ) => {
        if (currentValue) {
          const currentSteps: PlanIdStepDay[] = currentValue.steps.map(
            step => ({
              planId: currentValue.id,
              step,
              dayIndex: currentIndex,
            })
          );

          return previousValue.concat(currentSteps);
        }

        return previousValue;
      },
      []
    );

    return steps;
  }, [plans]);

  const planAlternatives = useMemo(() => {
    const alternativePois = alternatives.reduce(
      (previousValue: Model.Poi[], currentValue: IStepAlternatives) =>
        previousValue.concat(currentValue.pois),
      []
    );

    const excludedEmpties = alternativePois.filter(
      alternativePoi => alternativePoi
    );

    return helper.removeDuplicateValuesById<Model.Poi>(excludedEmpties);
  }, [alternatives]);

  const poiPlanStepDay = useCallback(
    (poiId: string): PlanIdStepDay | undefined =>
      planStepDays.find(
        memoizedStep =>
          memoizedStep.step.poi.id === poiId &&
          memoizedStep.dayIndex === dayIndex
      ),
    [dayIndex, planStepDays]
  );

  const isCurrentDayStep = useCallback(
    (poiId: string): boolean => !!poiPlanStepDay(poiId),
    [poiPlanStepDay]
  );

  const poiDayIndexes = useCallback(
    (poiId: string) => {
      const poisSteps: PlanIdStepDay[] = planStepDays.filter(
        (memoizedStep: PlanIdStepDay) => memoizedStep.step.poi.id === poiId
      );

      if (poisSteps.length > 0)
        return poisSteps.map(poisStep => poisStep.dayIndex); // for step poi dayIndexes like [0,1,2,...]

      return [
        planAlternatives.findIndex(
          alternativePoi => alternativePoi.id === poiId
        ) > -1
          ? -1
          : -2,
      ];
    },
    [planAlternatives, planStepDays]
  );

  const searchContainerClasses = [classes.searchContainer];
  if (!window.tconfig.SHOW_OVERVIEW)
    searchContainerClasses.push("trip-layout-container-height");
  else searchContainerClasses.push("trip-layout-container-height-tab");
  if (show) searchContainerClasses.push(classes.searchContainerOpen);
  else searchContainerClasses.push(classes.searchContainerClose);

  const cardButtonOnClick = useCallback(
    (poi: Model.Poi, from?: string, to?: string) => {
      const planStepDay = poiPlanStepDay(poi.id);
      if (tripReadOnly) {
        setShowCloneModal(true);
      } else if (planStepDay) {
        stepDelete(planStepDay.step.id);
      } else {
        stepAdd(currentPlanId, poi.id, from, to);
      }
    },
    [currentPlanId, poiPlanStepDay, stepAdd, stepDelete, tripReadOnly]
  );

  const focus = useCallback(
    (poi: Model.Poi) => {
      const planStepDay = poiPlanStepDay(poi.id);
      if (planStepDay) focusStep(planStepDay.step);
      else focusPoi(poi, false);
    },
    [focusPoi, focusStep, poiPlanStepDay]
  );

  const mainCategoryGroups = useMemo(() => {
    return [
      { id: "all", name: t("trips.categories.all") },
      ...poiCategoryGroups.map(group => ({ id: group.name, name: group.name })),
    ];
  }, [poiCategoryGroups, t]);

  const handleMainCategorySelect = (categoryId: string) => {
    setActiveMainCategory(categoryId);

    if (categoryId === "all") {
      const allCategories = poiCategoryGroups.flatMap(
        group => group.categories
      );
      setSelectedPoiCategories(allCategories);
    } else {
      const selectedGroup = poiCategoryGroups.find(
        group => group.name === categoryId
      );
      if (selectedGroup) {
        setSelectedPoiCategories(selectedGroup.categories);
      }
    }
  };

  const availableQuickFilters = useMemo(() => {
    if (!autoCompleteTags || autoCompleteTags.length === 0) {
      return [];
    }

    const seen = new Set<string>();
    return autoCompleteTags
      .filter(option => {
        if (seen.has(option.value)) return false;
        seen.add(option.value);
        return true;
      })
      .map(option => ({
        id: option.value,
        name: option.label,
      }));
  }, [autoCompleteTags]);

  const combinedQuery = useMemo(() => {
    if (selectedTags.length === 0) {
      return undefined;
    }
    return selectedTags;
  }, [selectedTags]);

  const filterButtonRef = React.useRef<HTMLButtonElement>(null);
  const [popupPosition, setPopupPosition] = useState<{
    top: number;
    right: number;
    placement: "top" | "bottom";
  }>({
    top: 0,
    right: 0,
    placement: "bottom",
  });

  useEffect(() => {
    if (showFilters && filterButtonRef.current) {
      const rect = filterButtonRef.current.getBoundingClientRect();
      const windowHeight = window.innerHeight;

      const estimatedPopupHeight = 400; // px
      const safetyMargin = 20; // px

      const spaceBelow = windowHeight - rect.bottom;
      const spaceAbove = rect.top;

      if (spaceBelow >= estimatedPopupHeight + safetyMargin) {
        setPopupPosition({
          top: rect.bottom + 8,
          right: window.innerWidth - rect.right,
          placement: "bottom",
        });
      } else if (spaceAbove >= estimatedPopupHeight + safetyMargin) {
        setPopupPosition({
          top: Math.max(100, rect.top - estimatedPopupHeight + 100),
          right: window.innerWidth - rect.right,
          placement: "top",
        });
      } else {
        if (spaceBelow >= spaceAbove) {
          setPopupPosition({
            top: rect.bottom + 8,
            right: window.innerWidth - rect.right,
            placement: "bottom",
          });
        } else {
          setPopupPosition({
            top: Math.max(100, rect.top - estimatedPopupHeight + 100),
            right: window.innerWidth - rect.right,
            placement: "top",
          });
        }
      }
    }
  }, [showFilters]);

  const sortedSelectedTags = useMemo(() => {
    const tagOrder = autoCompleteTags.map(tag => tag.value);
    return [...selectedTags].sort(
      (a, b) => tagOrder.indexOf(a) - tagOrder.indexOf(b)
    );
  }, [selectedTags, autoCompleteTags]);

  return (
    <div className={searchContainerClasses.join(" ")}>
      <div className="mb-16 md:mb-0 h-full">
        <div className="whitespace-nowrap py-4 border-b">
          <CustomSlider scrollSize={150}>
            {mainCategoryGroups.map((category, index) => (
              <div
                key={index}
                className={`text-sm !w-auto rounded-3xl border border-solid border-gray-300 my-0 py-[0.4375rem] px-4 cursor-pointer min-w-fit font-medium whitespace-nowrap ${
                  activeMainCategory === category.id
                    ? "text-white bg-primary-color"
                    : "text-black bg-white"
                }`}
                onClick={() => handleMainCategorySelect(category.id)}
              >
                <span className="font-medium">
                  {category.id === "all"
                    ? t("trips.categories.all")
                    : category.name}
                </span>
              </div>
            ))}
          </CustomSlider>
        </div>

        {availableQuickFilters.length > 0 && (
          <div className="py-4 border-b relative">
            <div className="relative">
              <div className="flex space-x-2 rtl:space-x-reverse py-1 min-h-[40px]">
                <CustomSlider scrollSize={150}>
                  {availableQuickFilters.map(filter => (
                    <button
                      key={`quick-filter-${filter.id}`}
                      className={`bg-zinc-100 px-4 py-[0.375rem] text-sm rounded-full whitespace-nowrap transition-all ${
                        selectedTags.includes(filter.name)
                          ? "border-2 border-primary-color text-primary-color font-medium shadow-sm"
                          : "border-2 border-zinc-100 text-gray-900"
                      }`}
                      onClick={() => {
                        if (selectedTags.includes(filter.name)) {
                          setSelectedTags(
                            selectedTags.filter(t => t !== filter.name)
                          );
                        } else {
                          setSelectedTags([...selectedTags, filter.name]);
                        }
                      }}
                    >
                      {filter.name}
                    </button>
                  ))}
                </CustomSlider>
              </div>
            </div>
          </div>
        )}

        <div className="py-4 relative w-full">
          <div className="flex items-center w-full">
            <div className="flex-1 relative w-full">
              <PoiSearchAutoComplete
                options={autoCompleteTags}
                isLoading={autoCompleteLoading && autoCompleteTags.length === 0}
                selectedOptionValues={selectedTags}
                onSelectedOptionChange={selectedOptions => {
                  setSelectedTags(selectedOptions.map(opt => opt.value));
                }}
                onCreateOption={input => {
                  if (input.length > 2 && !selectedTags.includes(input)) {
                    setSelectedTags([...selectedTags, input]);
                  }
                }}
                inputValue={poiCategorySearchText}
                onInputChange={searchText =>
                  setPoiCategorySearchText(searchText)
                }
                onKeyDown={e => {
                  if (
                    e.key === "Enter" &&
                    poiCategorySearchText.trim().length > 0
                  ) {
                    const newSearchTerm = poiCategorySearchText.trim();
                    if (!selectedTags.includes(newSearchTerm)) {
                      setSelectedTags(prev => [...prev, newSearchTerm]);
                    }
                    setPoiCategorySearchText("");
                  }
                }}
                placeHolder={t("trips.myTrips.exploreMore.placeholder")}
                isDisabled={selectedPoiCategoryIds.length === 0}
                t={t}
              />

              <SvgIcons.Search className="absolute start-4 top-1/2 transform -translate-y-1/2 text-gray-500" />
              <button
                className="absolute end-4 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                onClick={() => {
                  setPoiCategorySearchText("");
                }}
              >
                {poiCategorySearchText && <SvgIcons.SearchClose />}
              </button>
            </div>
            <div className="relative ms-2">
              <button
                className="py-3 px-4 rounded-full bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors flex items-center"
                onClick={() => setShowFilters(!showFilters)}
                aria-haspopup="true"
                aria-expanded={showFilters}
                ref={filterButtonRef}
              >
                <SvgIcons.Filter2 className="me-1" />
                {t("trips.filters.title")}
              </button>

              {showFilters && (
                <>
                  <div
                    className="fixed inset-0 z-40"
                    onClick={() => setShowFilters(false)}
                    style={{ pointerEvents: "all" }}
                  ></div>
                  <div
                    className="fixed w-80 bg-white rounded-lg shadow-xl z-50 border border-gray-200"
                    style={{
                      ...(popupPosition.placement === "bottom"
                        ? { top: `${popupPosition.top}px` }
                        : { top: `${popupPosition.top}px` }),
                      right: `${popupPosition.right}px`,
                      maxHeight: "70vh",
                    }}
                    onClick={e => e.stopPropagation()}
                  >
                    <div className="p-4 border-b flex justify-between items-center bg-white z-10 rounded-t-lg">
                      <h3 className="font-semibold text-gray-800">
                        {t("trips.filters.title")}
                      </h3>
                      <div className="flex items-center">
                        <button
                          className="text-sm bg-gray-100 hover:bg-gray-200 rounded-full px-3 py-1.5 text-gray-700 hover:text-gray-900 transition-colors me-3"
                          onClick={() => {
                            setSelectedPoiCategories([]);
                            setShowFilters(false);
                          }}
                        >
                          {t("trips.filters.clearAll")}
                        </button>
                        <button
                          onClick={() => setShowFilters(false)}
                          className="text-gray-400 hover:text-gray-700 transition-colors w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100"
                        >
                          <SvgIcons.SearchClose
                            width="18"
                            height="18"
                            className="text-gray-400 hover:text-gray-700 transition-colors flex items-center justify-center rounded-full hover:bg-gray-100"
                          />
                        </button>
                      </div>
                    </div>
                    <div className="max-h-[400px] overflow-y-auto custom-scrollbar">
                      <div className="p-4">
                        {activeMainCategory !== "all" && (
                          <div className="mb-3">
                            <h4 className="font-medium mb-3 text-gray-700 border-s-4 border-primary-color ps-2">
                              {
                                mainCategoryGroups.find(
                                  c => c.id === activeMainCategory
                                )?.name
                              }
                            </h4>
                            <div className="grid grid-cols-1 gap-2 ps-2">
                              {poiCategoryGroups
                                .find(
                                  group => group.name === activeMainCategory
                                )
                                ?.categories.map(category => (
                                  <div
                                    className="flex items-center hover:bg-gray-50 rounded-md p-1.5 -ms-1.5"
                                    key={`filter-category-${category.id}`}
                                  >
                                    <div className="relative w-5 h-5">
                                      <input
                                        type="checkbox"
                                        id={`category-${category.id}`}
                                        checked={selectedPoiCategoryIds.includes(
                                          category.id
                                        )}
                                        onChange={() => {
                                          const newCategories =
                                            selectedPoiCategoryIds.includes(
                                              category.id
                                            )
                                              ? selectedPoiCategories.filter(
                                                  c => c.id !== category.id
                                                )
                                              : [
                                                  ...selectedPoiCategories,
                                                  category,
                                                ];

                                          setSelectedPoiCategories(
                                            newCategories
                                          );
                                        }}
                                        className="appearance-none h-5 w-5 rounded-md bg-white border border-gray-300 checked:border-primary-color checked:bg-primary-color checked:border-0 focus:outline-none focus:ring-0 transition-colors"
                                      />
                                      <SvgIcons.Check2
                                        className={`absolute start-0.5 top-0.5 w-4 h-4 text-white pointer-events-none ${
                                          selectedPoiCategoryIds.includes(
                                            category.id
                                          )
                                            ? "opacity-100"
                                            : "opacity-0"
                                        }`}
                                        fill="white"
                                      />
                                    </div>
                                    <label
                                      htmlFor={`category-${category.id}`}
                                      className="ms-2.5 text-gray-700 cursor-pointer text-sm"
                                    >
                                      {category.name}
                                    </label>
                                  </div>
                                ))}
                            </div>
                          </div>
                        )}
                        {activeMainCategory === "all" && (
                          <div>
                            {poiCategoryGroups.map(group => (
                              <div
                                key={`filter-group-${group.name}`}
                                className="mb-5"
                              >
                                <h4 className="font-medium mb-3 text-gray-700 border-s-4 border-primary-color ps-2">
                                  {group.name}
                                </h4>
                                <div className="grid grid-cols-1 gap-2 ps-2">
                                  {group.categories.map(category => (
                                    <div
                                      className="flex items-center hover:bg-gray-50 rounded-md p-1.5 -ms-1.5"
                                      key={`filter-category-${category.id}`}
                                    >
                                      <div className="relative w-5 h-5">
                                        <input
                                          type="checkbox"
                                          id={`category-${category.id}`}
                                          checked={selectedPoiCategoryIds.includes(
                                            category.id
                                          )}
                                          onChange={() => {
                                            const newCategories =
                                              selectedPoiCategoryIds.includes(
                                                category.id
                                              )
                                                ? selectedPoiCategories.filter(
                                                    c => c.id !== category.id
                                                  )
                                                : [
                                                    ...selectedPoiCategories,
                                                    category,
                                                  ];

                                            setSelectedPoiCategories(
                                              newCategories
                                            );
                                          }}
                                          className="appearance-none h-5 w-5 rounded-md bg-white border border-gray-300 checked:border-primary-color checked:bg-primary-color checked:border-0 focus:outline-none focus:ring-0 transition-colors"
                                        />
                                        <SvgIcons.Check2
                                          className={`absolute start-0.5 top-0.5 w-4 h-4 text-white pointer-events-none ${
                                            selectedPoiCategoryIds.includes(
                                              category.id
                                            )
                                              ? "opacity-100"
                                              : "opacity-0"
                                          }`}
                                          fill="white"
                                        />
                                      </div>
                                      <label
                                        htmlFor={`category-${category.id}`}
                                        className="ms-2.5 text-gray-700 cursor-pointer text-sm"
                                      >
                                        {category.name}
                                      </label>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>

        {sortedSelectedTags.length > 0 && (
          <div className="flex flex-wrap gap-2 px-4 py-3 border-b">
            {sortedSelectedTags.map((tag, index) => (
              <Badge
                key={`selected-tag-${index}-${tag}`}
                name={tag}
                onClose={() => {
                  setSelectedTags(prev => prev.filter(t => t !== tag));
                }}
              />
            ))}
            <button
              className="text-xs bg-gray-200 rounded-full px-3 py-1 text-gray-800 hover:bg-gray-300 transition-colors"
              onClick={() => {
                setSelectedTags([]);
              }}
            >
              {t("trips.filters.clearAll")}
            </button>
          </div>
        )}
        <SearchPlace
          query={combinedQuery}
          poiCategoryIds={selectedPoiCategoryIds}
          search={openSearchPoi}
          focus={focus}
          cardButtonOnClick={cardButtonOnClick}
          isCurrentDayStep={isCurrentDayStep}
          getDayIndexes={poiDayIndexes}
          gygTourIds={gygTourIds}
          bbTourIds={bbTourIds}
          viatorTourIds={viatorTourIds}
          toristyTourIds={toristyTourIds}
          toursLoading={toursLoading}
          onBookingClick={(poi: Model.Poi) => {
            focus(poi);
            onBookingClick();
          }}
          t={t}
        />
      </div>
      {showCloneModal && (
        <CloneModal
          sharedTripHash={tripHash ?? ""}
          onCancel={() => setShowCloneModal(false)}
        />
      )}
    </div>
  );
};

export default SearchContainer;
